// SEPLA 3D hero — six-chapter story, profile generator and camera keyframes (pure logic, no three.js imports)

export const NB = 6;

export const BEATS = [
  {
    short: "Coil",
    name: "Steel coil",
    machine: "Mill coil → flat sheet",
    copy: "Food-grade stainless steel unrolls into a flat sheet, ready for precise cutting.",
  },
  {
    short: "Pressing",
    name: "Pressing",
    machine: "Blanking die · deep-draw press",
    copy: "A circular blank is cut, then deep-drawn into a seamless, tapered ice bucket.",
  },
  {
    short: "Shaping",
    name: "Shaping",
    machine: "Trimming · rim rolling · ring handles",
    copy: "The rim is trimmed and rolled smooth, then polished ring handles are fitted.",
  },
  {
    short: "Polishing",
    name: "Polishing",
    machine: "Buffing and finishing",
    copy: "Hand-finished to a mirror shine that resists stains and corrosion.",
  },
  {
    short: "Quality",
    name: "Quality check",
    machine: "Laser scan · permanent marking",
    copy: "A laser scans every surface and permanently marks each piece as SEPLA.",
  },
  {
    short: "Range",
    name: "The range",
    machine: "Sepla product range",
    copy: "Built in steel. Built for scale.",
    cta: true,
  },
];

export const clamp = (x, a = 0, b = 1) => Math.max(a, Math.min(b, x));
export const sst = (a, b, x) => { const u = clamp((x - a) / (b - a)); return u * u * u * (u * (u * 6 - 15) + 10); };
export const lerp = (a, b, u) => a + (b - a) * u;

// Animation channels as functions of story time T (0..NB). Chapter i owns [i, i+1).
export function channels(T) {
  return {
    unroll: sst(0.12, 0.82, T),
    blank: sst(1.1, 1.38, T),
    sheetAway: sst(1.42, 1.82, T),
    draw: sst(1.45, 1.95, T),
    trim: sst(2.12, 2.48, T),
    curl: sst(2.34, 2.74, T),
    handles: sst(2.56, 2.96, T),
    polish: sst(3.14, 3.82, T),
    scan: sst(4.12, 4.52, T),
    laser: sst(4.42, 4.92, T),
    finale: sst(5.14, 5.92, T),
  };
}

// Camera keyframes, one per chapter. The stage sits right on desktop and centres on mobile.
export const CAM = [
  { dx: 0, y: 1.0, z: 7.2, lookY: 0.35 },
  { dx: 0.15, y: 1.6, z: 7.0, lookY: 0.6 },
  { dx: 0, y: 1.45, z: 6.9, lookY: 0.92 },
  { dx: 0.3, y: 1.5, z: 6.5, lookY: 0.92 },
  { dx: 0, y: 1.35, z: 6.0, lookY: 0.9 },
  { dx: 0, y: 1.6, z: 8.6, lookY: 0.6 },
];

// Outer wall radius factor at wall position t (0 = foot, 1 = rim). taper 0 = straight pot, 1 = ice bucket.
const wallF = (t, taper) => (0.98 + 0.025 * t) * (1 + taper * (0.26 * t - 0.1));

// Fixed-point lathe profile: capsule base, outer wall, rolled rim, inner wall and floor.
export const NP = 32;
export function potProfile({ R, H, wall = 0.055, bead = 0, jag = 0, taper = 0 }) {
  const h = Math.max(H, 0.05);
  const baseY = Math.min(0.035, h * 0.2);
  const capH = Math.min(0.16, Math.max(0.035, h * 0.13));
  const rimY = Math.max(h, capH + 0.025);
  const pts = [];

  // underside and capsule-base foot (6)
  const Rb = R * (1 - 0.1 * taper);
  pts.push([0, baseY], [Rb * 0.42, baseY], [Rb * 0.76, baseY + 0.004]);
  pts.push([Rb * 0.88, baseY + capH * 0.2], [Rb * 0.95, baseY + capH * 0.58], [Rb * 0.98, baseY + capH]);

  // outer wall (8)
  for (let k = 0; k < 8; k++) {
    const t = (k + 1) / 8;
    const y = baseY + capH + (rimY - baseY - capH) * t;
    const r = R * wallF(t, taper);
    const wave = jag > 0 && t > 0.68 ? jag * 0.035 * Math.sin(k * 2.4) : 0;
    pts.push([r, y + wave]);
  }

  // rolled rim (5)
  const rimR = R * 1.005 * (1 + taper * 0.16);
  for (let k = 0; k < 5; k++) {
    const t = (k + 1) / 5;
    const curl = bead * Math.sin(t * Math.PI);
    pts.push([
      rimR + curl * 0.055 - wall * Math.max(0, t - 0.45),
      rimY + curl * 0.075 + (jag > 0 ? jag * 0.018 * Math.sin(k * 1.9) : 0),
    ]);
  }

  // inner wall (8)
  const floorY = Math.max(baseY + wall * 0.7, 0.045);
  let innerB = rimR - wall;
  for (let k = 0; k < 8; k++) {
    const t = (k + 1) / 8;
    const straight = (rimR - wall) * (1 - 0.08 * t * t);
    const tapered = R * wallF(1 - t, taper) - wall;
    const r = lerp(straight, tapered, taper);
    const y = rimY + (floorY - rimY) * t;
    pts.push([r, y]);
    innerB = r;
  }

  // inner floor (5)
  pts.push([innerB * 0.85, floorY], [innerB * 0.63, floorY * 0.96]);
  pts.push([innerB * 0.37, floorY * 0.92], [innerB * 0.15, floorY * 0.9], [0, floorY * 0.88]);

  return pts; // 32 points
}

// Outer radius of the profile at height y (used to place handles, scan ring and laser marking).
export function wallRadiusAt({ R, H, taper = 0 }, y) {
  const h = Math.max(H, 0.05);
  const baseY = Math.min(0.035, h * 0.2);
  const capH = Math.min(0.16, Math.max(0.035, h * 0.13));
  const rimY = Math.max(h, capH + 0.025);
  const t = clamp((y - baseY - capH) / Math.max(0.001, rimY - baseY - capH));
  return R * wallF(t, taper);
}
export const rimRadius = ({ R, taper = 0 }) => R * 1.005 * (1 + taper * 0.16);
